import { getDate8AsInt, isset, sameId, ValueRange } from "@cawita/core-front";
import { Event, EventReservation, Position, Stay, User } from "@shared/models";
import { isAfter, isBefore, startOfDay, subDays } from "date-fns";
import { getEndDateFromDate8, getStartDateFromDate8 } from "./date.utils";

export function getEventDateFiltersFromStayOrFilter(stay: Stay, range?: ValueRange<Date>) {
    const today = getDate8AsInt(new Date());
    const minDate8 = stay?.startDate ? Math.max(today, stay.startDate) : today;
    const minDate = getStartDateFromDate8(minDate8);
    const maxDate = stay?.endDate ? getEndDateFromDate8(stay.endDate) : undefined;

    let startDate = minDate;
    let endDate = maxDate;
    if (range?.start) startDate = Math.max(minDate, getStartDateFromDate8(getDate8AsInt(range.start)));
    if (range?.end) endDate = maxDate ? Math.min(maxDate, getEndDateFromDate8(getDate8AsInt(range.end))) : getEndDateFromDate8(getDate8AsInt(range.end));

    return { startDate, endDate };
}

export function getEventBookingStart(event?: Event): Date {
    if (!event) return null;
    if (!isset(event.reservationDelay)) return startOfDay(new Date());
    if (event.reservationDelay === 0) return startOfDay(event.startDateAsObject);
    return startOfDay(subDays(event.startDateAsObject, event.reservationDelay));
}

export function getEventBookingEnd(event?: Event): Date {
    if (!event) return null;
    return event.endDateAsObject;
}

export function eventIsBookable(event?: Event) {
    const start = getEventBookingStart(event);
    if (!start) return false;
    return !isBefore(new Date(), start);
}

export function eventHasEnded(event?: Event) {
    const end = getEventBookingEnd(event);
    if (!end) return true;
    return isAfter(new Date(), end);
}

export function eventIsOwnedBy(event?: Event, user?: User) {
    if (!event || !user) return false;
    return sameId(event.owner, user);
}

export function hasEventConcurrentBookingCountReached(event: Event, reservation: EventReservation): boolean {
    if (!event?.maxSimultaneousReservations) return false;
    if (!reservation) return false;

    const maxAttendees = event?.maxSimultaneousReservations ?? 0;
    const externalAttendeesCount = reservation?.externalAttendees?.length ?? 0;
    if (reservation?.doesUserAttend) return (externalAttendeesCount + 1) >= maxAttendees;
    return externalAttendeesCount >= maxAttendees;
}

export function getEventPosition(event: Event) {
    if (!event) return null;
    if (!isset(event?.xPos) || !isset(event?.yPos)) return null;
    return { xPos: event?.xPos, yPos: event?.yPos };
}

export function getEventPlacePosition(event: Event): Position {
    if (!event?.place) return null;
    if (!isset(event?.place?.xPos) || !isset(event?.place?.yPos)) return null;
    return { xPos: event?.place?.xPos, yPos: event?.place?.yPos };
}